import { X } from 'lucide-react';
import Combobox from '@/components/combo-box';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface TeamUserOption {
  id: string;
  name: string;
  email: string;
}

interface CourseAssignmentTeamDialogProps {
  open: boolean;
  canEdit: boolean;
  teamDialogMode: 'create' | 'edit';
  teamName: string;
  selectedTeamMemberOption: string;
  selectedTeamUserIds: string[];
  currentUserId?: string;
  filteredTeamUserOptions: TeamUserOption[];
  selectedTeamUsers: TeamUserOption[];
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onTeamNameChange: (value: string) => void;
  onSelectedTeamMemberOptionChange: (value: string) => void;
  onSelectedTeamUserIdsChange: (updater: (prev: string[]) => string[]) => void;
  onRemoveTeamMember: (userId: string) => void;
  onSubmit: () => void;
}

const CourseAssignmentTeamDialog = ({
  open,
  canEdit,
  teamDialogMode,
  teamName,
  selectedTeamMemberOption,
  selectedTeamUserIds,
  currentUserId,
  filteredTeamUserOptions,
  selectedTeamUsers,
  isSubmitting,
  onOpenChange,
  onTeamNameChange,
  onSelectedTeamMemberOptionChange,
  onSelectedTeamUserIdsChange,
  onRemoveTeamMember,
  onSubmit,
}: CourseAssignmentTeamDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto p-0 sm:max-w-[940px]" showCloseButton={false}>
        <DialogHeader className="border-b px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>{teamDialogMode === 'edit' ? 'Edit team' : 'Create team'}</DialogTitle>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted/70 hover:bg-muted"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="space-y-4 border-b p-5 lg:border-r lg:border-b-0">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Team name <span className="text-destructive">*</span>
              </label>
              <Input
                value={teamName}
                onChange={(event) => onTeamNameChange(event.target.value)}
                placeholder="Enter team name"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Add member</label>
              <Combobox
                options={filteredTeamUserOptions.map((user) => ({
                  value: user.id,
                  label: user.name,
                  searchTerms: user.email,
                  extras: <span className="mr-2 text-xs text-muted-foreground">{user.email}</span>,
                }))}
                selectedOption={selectedTeamMemberOption}
                onSelect={(value) => {
                  if (!value) return;
                  if (selectedTeamUserIds.includes(value)) return;
                  onSelectedTeamUserIdsChange((prev) => [...prev, value]);
                  onSelectedTeamMemberOptionChange('');
                }}
                placeholder="Search user"
                searchable
              />
            </div>
          </div>

          <div className="p-5">
            <p className="mb-2 text-sm font-medium">Selected users</p>
            {selectedTeamUsers.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                {selectedTeamUsers.map((user) => {
                  const isSelfLocked =
                    teamDialogMode === 'create' && !canEdit && currentUserId === user.id;
                  return (
                    <span
                      key={user.id}
                      className="inline-flex items-center gap-2 rounded-md bg-muted px-2.5 py-1.5 text-sm"
                    >
                      {user.name}
                      <button
                        type="button"
                        disabled={isSelfLocked}
                        className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${
                          isSelfLocked
                            ? 'cursor-not-allowed text-muted-foreground/50'
                            : 'text-muted-foreground hover:bg-background'
                        }`}
                        onClick={() => onRemoveTeamMember(user.id)}
                      >
                        <X className="h-3 w-3" />
                        <span className="sr-only">Remove user</span>
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No users selected yet.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end border-t px-5 py-4">
          <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
            {teamDialogMode === 'edit' ? 'Update' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CourseAssignmentTeamDialog;
